import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import {
    PeekSettings,
    Service,
    ServiceConfigoption,
    ServiceStatus,
} from '@/modules/Service/types'
import ViewSetting from '@/modules/Service/components/Detail/Templates/Conversant/shared/ViewSetting'

interface ServiceEditFormProps {
    service: Service
    configoptions?: ServiceConfigoption[]
    onSuccess?: () => void
    showActions?: boolean
    addons?: Partial<
        Service & {
            configoptions: ServiceConfigoption[]
        }
    >[]
}

// Commitment fields surfaced in the config view (mirrors the Statement tab).
const CONFIG_PEEK_SETTINGS: PeekSettings[] = [
    'name',
    'commitment_value',
    'commitment_time',
    'commitment_prepaid',
    'commitment_price_per_unit',
    'commitment_price_total',
    'statementable',
    'commitment_available',
]

// Reuses ViewSetting (same component the Statement tab renders) for the main
// service and each addon. view_setting=true = read-only; editing is only enabled
// while a service is waiting-confirm, which matches the per-service API guard.
const ServiceEditForm: React.FC<ServiceEditFormProps> = ({
    service,
    configoptions,
    addons,
}) => {
    const { t } = useTranslation()
    // Guard against null (API may return results: null when there are no addons —
    // a default param only covers undefined, not null).
    const safeAddons = addons ?? []
    const mainService = {
        ...service,
        configoptions: configoptions ?? service.configoptions ?? [],
    }

    const renderSection = (svc: Service, label?: string, key?: React.Key) => (
        <div key={key} className="space-y-2">
            {label && (
                <Typogaphy size="small" className="font-medium text-gray-700">
                    {label}
                </Typogaphy>
            )}
            <ViewSetting
                service={svc}
                view_setting={
                    svc.service_status !== ServiceStatus.WaitingConfirm
                }
                peek_settings={CONFIG_PEEK_SETTINGS}
            />
        </div>
    )

    return (
        <div className="space-y-4">
            {renderSection(mainService, t('main-configuration'))}

            {safeAddons.length > 0 && (
                <div className="space-y-3">
                    <Typogaphy
                        size="small"
                        className="font-medium text-gray-700"
                    >
                        {t('addon-services')} ({safeAddons.length})
                    </Typogaphy>
                    {safeAddons.map((addon, index) =>
                        renderSection(
                            addon as Service,
                            addon?.service_name || `Addon ${index + 1}`,
                            `addon-${addon.service_id || index}`
                        )
                    )}
                </div>
            )}
        </div>
    )
}

export default ServiceEditForm
